import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, StatusBadge, EmptyState } from "@/components/common/Primitives";
import { fmtDate } from "@/lib/format";
import {
  TASK_STATUS_MAP, PRIORITY_MAP, REF_MODULE_OPTIONS, REF_MODULE_LABELS,
  TASK_ORIGIN_MAP, MS_SYNC_STATUS_MAP,
} from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Loader2, CheckSquare, AlertTriangle, Bot, User, Link2 } from "lucide-react";

export default function Tasks() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => { if (params.get("neu")) { setFormOpen(true); setParams({}); } }, [params, setParams]);

  const isBoard = filter === "board";

  const { data, isLoading } = useQuery({
    queryKey: ["tasks", filter],
    queryFn: async () => {
      if (isBoard) return (await api.get("/tasks/board")).data;
      const qs = filter === "mine" ? "?mine=true" : filter === "offen" ? "?status=offen" : "";
      return (await api.get(`/tasks${qs}`)).data;
    },
  });

  const update = async (id, changes) => {
    try { await api.put(`/tasks/${id}`, changes); qc.invalidateQueries({ queryKey: ["tasks"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  const rows = data?.items || [];
  const msStatus = data?.microsoft_status;

  return (
    <div>
      <PageHeader title="Aufgaben" description="Manuelle, automatische und Microsoft-365-Aufgaben"
        actions={can(user?.role, "tasks:write") && (
          <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setFormOpen(true)} data-testid="new-task-button">
            <Plus className="h-4 w-4" /> Neue Aufgabe
          </Button>
        )} />

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="task-filter-all">Alle</TabsTrigger>
          <TabsTrigger value="mine" data-testid="task-filter-mine">Meine</TabsTrigger>
          <TabsTrigger value="offen" data-testid="task-filter-open">Offen</TabsTrigger>
          <TabsTrigger value="board" data-testid="task-filter-board">Zentrale Übersicht</TabsTrigger>
        </TabsList>
      </Tabs>

      {isBoard && msStatus && msStatus !== "connected" && (
        <div className="mb-3 rounded-md border border-dashed bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="task-board-ms-banner">
          {msStatus === "not_configured" && "Microsoft 365 ist nicht konfiguriert – es werden nur ERP-Aufgaben angezeigt."}
          {msStatus === "disconnected" && "Microsoft 365 ist nicht verbunden – es werden nur ERP-Aufgaben angezeigt. Verbindung unter Integrationen."}
          {msStatus === "error" && "Microsoft-365-Aufgaben konnten nicht geladen werden – es werden nur ERP-Aufgaben angezeigt."}
        </div>
      )}

      {isLoading ? null : rows.length === 0 ? (
        <EmptyState icon={CheckSquare} title="Keine Aufgaben" description="Es sind keine Aufgaben vorhanden." />
      ) : (
        <div className="space-y-2">
          {rows.map((t) => (
            <div key={t.id} data-testid={`task-row-${t.id}`}
              className={`flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between ${t.overdue ? "border-destructive/30 bg-destructive/5" : "border-border"}`}>
              <div className="flex items-start gap-3 min-w-0">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                  {t.source === "automatisch" ? <Bot className="h-4 w-4 text-accent" /> : <User className="h-4 w-4 text-muted-foreground" />}
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium">{t.title}</span>
                    {t.overdue && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />}
                    {isBoard && t.origin && <StatusBadge status={t.origin} map={TASK_ORIGIN_MAP} />}
                    {t.ref_module && (
                      <Badge variant="outline" className="gap-1 text-[10px]"><Link2 className="h-3 w-3" />{REF_MODULE_LABELS[t.ref_module] || t.ref_module}{t.ref_label ? `: ${t.ref_label}` : ""}</Badge>
                    )}
                    {t.ms_sync_status && <StatusBadge status={t.ms_sync_status} map={MS_SYNC_STATUS_MAP} />}
                  </div>
                  {t.description && <p className="truncate text-xs text-muted-foreground">{t.description}</p>}
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                    <span>Fällig: {fmtDate(t.due_date)}</span>
                    {t.customer_name && <span>· {t.customer_name}</span>}
                    {t.assigned_user_name && <span>· {t.assigned_user_name}</span>}
                    <StatusBadge status={t.priority} map={PRIORITY_MAP} />
                    {t.ms_last_error && <span className="text-destructive">· {t.ms_last_error}</span>}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {t.origin && t.origin !== "erp" ? (
                  <StatusBadge status={t.status} map={TASK_STATUS_MAP} />
                ) : can(user?.role, "tasks:write") ? (
                  <Select value={t.status} onValueChange={(v) => update(t.id, { status: v })}>
                    <SelectTrigger className="h-8 w-40 text-xs" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TASK_STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <StatusBadge status={t.status} map={TASK_STATUS_MAP} />}
              </div>
            </div>
          ))}
        </div>
      )}

      <TaskForm open={formOpen} onOpenChange={setFormOpen} onSaved={() => qc.invalidateQueries({ queryKey: ["tasks"] })} />
    </div>
  );
}

const EMPTY = { title: "", description: "", type: "manuell", priority: "normal", due_date: "", customer_id: "", ref_module: "none", ref_label: "", ms_sync_target: "none" };

function TaskForm({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { data: custData } = useQuery({ queryKey: ["customers-min-tasks"], queryFn: async () => (await api.get("/customers?limit=200")).data, enabled: open });
  const { data: ms } = useQuery({ queryKey: ["ms-status"], queryFn: async () => (await api.get("/microsoft/status")).data, enabled: open });

  const msConnected = ms?.status === "connected";
  useEffect(() => { if (open) setForm(EMPTY); }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Bitte Titel eingeben");
    setSaving(true);
    const payload = { ...form, ref_module: form.ref_module === "none" ? "" : form.ref_module };
    try { await api.post("/tasks", payload); toast.success("Aufgabe angelegt"); onSaved(); onOpenChange(false); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Neue Aufgabe</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Titel</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title-input" /></div>
          <div className="space-y-1.5"><Label>Beschreibung</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Priorität</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Fällig am</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="task-due-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Kunde (optional)</Label>
            <Select value={form.customer_id || "none"} onValueChange={(v) => set("customer_id", v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Kein Kunde" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Kein Kunde</SelectItem>
                {(custData?.items || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>ERP-Vorgang (optional)</Label>
              <Select value={form.ref_module} onValueChange={(v) => set("ref_module", v)}>
                <SelectTrigger data-testid="task-ref-module"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Kein Bezug</SelectItem>
                  {REF_MODULE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Bezeichnung / Referenz</Label>
              <Input value={form.ref_label} onChange={(e) => set("ref_label", e.target.value)} placeholder="z. B. Artikel-Nr." disabled={form.ref_module === "none"} data-testid="task-ref-label" /></div>
          </div>
          <div className="space-y-1.5"><Label>Microsoft 365 Synchronisation</Label>
            <Select value={form.ms_sync_target} onValueChange={(v) => set("ms_sync_target", v)}>
              <SelectTrigger data-testid="task-ms-sync"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Keine</SelectItem>
                <SelectItem value="todo">Microsoft To Do (persönlich)</SelectItem>
                <SelectItem value="planner">Microsoft Planner (Team)</SelectItem>
              </SelectContent>
            </Select>
            {form.ms_sync_target !== "none" && !msConnected && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400">Microsoft 365 ist nicht verbunden – die Aufgabe wird gespeichert und als „Sync ausstehend“ markiert.</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={saving} data-testid="task-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
